import React, { cloneElement, useState } from "react";
import { Calendar, DateLocalizer, Event } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Card } from "react-bootstrap";
import SimpleModal from "../common/simple-modal";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
//import { HolidayList, UnavailableDays } from "lib/data/dummy-appointments";
import { CALENDER_COLORS, COLOR_BY_DAY, UNAVAILABLE_DAYS } from "lib/constants";
import FlexBox from "../common/flex-box";
import { getPropsAsArrayFromArray } from "lib/utils/array-utils";
import { areDatesEqual, formatDate, getLocalISO } from "lib/utils/date-utils";
import { isEmpty } from "lib/utils/string-utils";

let navigate = {
  PREVIOUS: "PREV",
  NEXT: "NEXT",
  TODAY: "TODAY",
  DATE: "DATE",
};

//const holidayList = HolidayList.map((d) => d.date);

const ViewNamesGroup = ({ views: viewNames, view, messages, onView }) => {
  return viewNames.slice(0, 3).map((name: string) => (
    <button
      type="button"
      key={name}
      className={view === name ? "rbc-active" : ""}
      onClick={() => onView(name)}
    >
      {messages[name]}
    </button>
  ));
};

const CustomToolbar = ({
  // date, // available, but not used here
  label,
  localizer: { messages },
  onNavigate,
  onView,
  view,
  views,
}) => {
  return (
    <div className="rbc-toolbar">
      <span className={"rbc-btn-group examples--custom-toolbar"}>
        <button
          type="button"
          onClick={() => onNavigate(navigate.PREVIOUS)}
          aria-label={messages.previous}
        >
          <FaArrowLeft />
        </button>
        <button
          type="button"
          onClick={() => onNavigate(navigate.TODAY)}
          aria-label={messages.today}
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => onNavigate(navigate.NEXT)}
          aria-label={messages.next}
        >
          <FaArrowRight />
        </button>
      </span>

      <span className="rbc-toolbar-label">{label}</span>

      <span className="rbc-btn-group">
        <ViewNamesGroup
          view={view}
          views={views}
          messages={messages}
          onView={onView}
        />
      </span>
    </div>
  );
};

interface AppointmentCalendarProps {
  localizer: DateLocalizer;
  title?: string;
  action?: React.ReactNode;
  showHolidays: boolean;
  calendarFilters?: React.ReactNode;
  showUnavailable: boolean;
  showBookings?: boolean;
  holidayList?: Event[];
  unavailableDays?: Event[];
  practitionerBookings?: Event[];
}

const getEventWrapperClassName = (event: Event) => {
  return event?.resource?.type ?? "";
};

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  localizer,
  title,
  action,
  showHolidays,
  calendarFilters,
  showUnavailable,
  showBookings,
  holidayList,
  unavailableDays,
  practitionerBookings,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const dayPropGetter = (date: Date) => {
    //const formattedDate = DateTime.fromJSDate(date).toISODate();
    if (
      showHolidays &&
      getPropsAsArrayFromArray(holidayList, "start")?.some((currentDate) =>
        areDatesEqual(currentDate, date)
      )
    ) {
      return {
        style: {
          backgroundColor: CALENDER_COLORS.HOLIDAY,
        },
      };
    } else if (
      showUnavailable &&
      getPropsAsArrayFromArray(unavailableDays, "start")?.some((currentDate) =>
        areDatesEqual(currentDate, date)
      )
    ) {
      return {
        style: {
          backgroundColor: CALENDER_COLORS.UNAVAILABLE,
        },
      };
    } else if (
      showBookings &&
      getPropsAsArrayFromArray(practitionerBookings, "start")?.some(
        (currentDate) => areDatesEqual(currentDate, date)
      )
    ) {
      const day = date.getDay();

      return {
        style: {
          backgroundColor: COLOR_BY_DAY[day] || CALENDER_COLORS.DEFAULT,
        },
      };
    }

    if (date === new Date()) {
      return {
        style: {
          backgroundColor: CALENDER_COLORS.TODAY,
        },
      };
    } else {
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      return {
        style: {
          backgroundColor: isWeekend
            ? CALENDER_COLORS.WEEKEND
            : CALENDER_COLORS.DEFAULT,
        },
      };
    }
  };
  const getTitle = (event: Event) => {
    if (event.title === "Unavailable") {
      return "Day";
    }

    if (event.title === "holiday") {
      return "Holiday";
    }

    return "Booking";
  };

  return (
    <Calendar
      className="w-100"
      localizer={localizer}
      startAccessor="start"
      events={[]
        .concat(holidayList ?? [])
        .concat(unavailableDays ?? [])
        .concat(practitionerBookings ?? [])
        .map((event) => ({ ...event, allDay: true }))}
      endAccessor="end"
      onSelectEvent={handleEventClick}
      style={{ height: 600 }}
      components={{
        toolbar: CustomToolbar,
        eventWrapper: ({ event, children }: any) => {
          const isPractitionerBooking =
            event?.resource?.type === UNAVAILABLE_DAYS.PRACTITIONER_BOOKINGS;

          const updatedChild = cloneElement(children, {
            className: `${children.props.className} ${getEventWrapperClassName(
              event
            )}`,
            style: {
              ...children.props.style,
              backgroundColor: "#ffffff", // white
              color: "#000000", // black
              fontSize: "12px",
            },
          });

          return (
            <div>
              {updatedChild}
              {isPractitionerBooking && (
                <div
                  style={{
                    fontSize: "10px",
                    marginTop: "2px",
                    marginLeft: "4px",
                  }}
                >
                  All Day
                </div>
              )}
            </div>
          );
        },
      }}
      dayPropGetter={dayPropGetter}
    />
  );

  // return (
  //   <>
  //     <Card className="shadow-lg w-100">
  //       <Card.Header className="p-3">
  //         <Card.Title>
  //           <FlexBox>
  //             {title ? <h3>{title}</h3> : <>{action}</>}
  //             {/* {calendarFilters && calendarFilters} */}
  //           </FlexBox>
  //         </Card.Title>
  //       </Card.Header>
  //       <Card.Body>
  //         <div className="border rounded p-2 shadow-sm bg-light">
  //           <Calendar
  //             localizer={localizer}
  //             startAccessor="start"
  //             events={[]
  //               .concat(holidayList ?? [])
  //               .concat(unavailableDays ?? [])
  //               .concat(practitionerBookings ?? [])
  //               .map((event) => ({ ...event, allDay: true }))}
  //             endAccessor="end"
  //             onSelectEvent={handleEventClick}
  //             style={{ height: 600 }}
  //             components={{
  //               toolbar: CustomToolbar,
  //               eventWrapper: ({ event, children }: any) => {
  //                 const isPractitionerBooking =
  //                   event?.resource?.type ===
  //                   UNAVAILABLE_DAYS.PRACTITIONER_BOOKINGS;

  //                 const updatedChild = cloneElement(children, {
  //                   className: `${
  //                     children.props.className
  //                   } ${getEventWrapperClassName(event)}`,
  //                   style: {
  //                     ...children.props.style,
  //                     backgroundColor: "#ffffff", // white
  //                     color: "#000000", // black
  //                     fontSize: "12px",
  //                   },
  //                 });

  //                 return (
  //                   <div>
  //                     {updatedChild}
  //                     {isPractitionerBooking && (
  //                       <div
  //                         style={{
  //                           fontSize: "10px",
  //                           marginTop: "2px",
  //                           marginLeft: "4px",
  //                         }}
  //                       >
  //                         All Day
  //                       </div>
  //                     )}
  //                   </div>
  //                 );
  //               },
  //             }}
  //             dayPropGetter={dayPropGetter}
  //           />
  //         </div>
  //       </Card.Body>
  //     </Card>

  //     {/* <SimpleModal
  //       open={isModalOpen}
  //       close={() => setIsModalOpen(false)}
  //       title={"Practitioner Bookings"}
  //       size="sm"
  //     >
  //       {selectedEvent && (
  //         <div>
  //           <h5 className="mb-3 text-primary">
  //             {getTitle(selectedEvent)}:&nbsp;
  //             {isEmpty(selectedEvent.title as string)
  //               ? "Unnamed"
  //               : selectedEvent.title}
  //           </h5>
  //           <p>
  //             <strong>From:</strong>{" "}
  //             {formatDate(getLocalISO(selectedEvent.start))}
  //           </p>
  //           <p>
  //             <strong>To:</strong> {formatDate(getLocalISO(selectedEvent.end))}
  //           </p>
  //         </div>
  //       )}
  //     </SimpleModal> */}
  //   </>
  // );
};

export default AppointmentCalendar;
